import { Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { useMemo } from "react";

import CardWithTitle from "../../../../components/CardWithTitle";
import { compose, lifecycle, withState } from "recompose";
import { connect } from "react-redux";
import { withParcelEdit } from "../../hocs";
import {
  clearVaultDocumentTypesAndDims,
  fetchVaultDocumentTypesAndDims,
  uploadDocumentToVault,
} from "../../actions";
import {
  getSelectedVaultDocumentType,
  getVaultDocumentTypesKeyValue,
} from "../../selectors";
import { deliveryEditValidators } from "../../../../models/validators";
import { Banner, Button, FormControl, withPrompt } from "@dpdgroupuk/mydpd-ui";
import { BANNERS_TYPES } from "@dpdgroupuk/mydpd-enums";
import classNames from "classnames";
import { Field } from "redux-form";
import { Fields } from "../../../../constants/forms";
import styles from "./UploadDocumentToVault.module.scss";
import FileInput from "../../../../components/FileInput";
import { withAppLoader, withNotifier } from "@dpdgroupuk/mydpd-app";
import { isDocumentTypeUploaded, isRealPdfFile } from "../../helpers";
import * as M from "../../../../constants/strings";

const UploadDocumentToVault = ({
  handleSubmit,
  onSubmit,
  error,
  disableSubmit,
  documentTypes,
  selectedDocumentType,
  onChangeDocumentType,
}) => {
  const acceptType = useMemo(() => {
    return selectedDocumentType?.fileTypes.length
      ? `.${selectedDocumentType.fileTypes[0]}`
      : "*";
  }, [selectedDocumentType?.fileTypes]);

  return (
    <Col className="col-12 p-0">
      <CardWithTitle title={M.DOCUMENT_UPLOAD}>
        <Row className="align-items-baseline">
          <Col sm={3} className="pb-2">
            {M.DOCUMENT_TYPE}
          </Col>
          <Col sm={9}>
            {/*  // NOTE: wrap by Row and Col for same width with fileInput */}
            <Row>
              <Col sm={10}>
                <Field
                  component={FormControl.Dropdown}
                  label={
                    selectedDocumentType
                      ? M.DOCUMENT_TYPE
                      : M.PLEASE_SELECT_DOCUMENT_TYPE
                  }
                  name={Fields.DOCUMENT_TYPE}
                  values={documentTypes}
                  textTransform={M.UPPERCASE}
                  onChange={onChangeDocumentType}
                  required
                />
              </Col>
            </Row>
          </Col>
          <Col sm={3} className="pb-2">
            {M.FILE_LOCATION}
          </Col>
          <Col sm={9} className="text-right">
            <Field
              component={FileInput}
              label={M.FILE_LOCATION}
              accept={acceptType}
              name={Fields.DOCUMENT}
              required
            />
          </Col>
          {selectedDocumentType && (
            <>
              <Col xs={12}>{M.DOCUMENT_UPLOAD_IMPORTANT}</Col>
              <Col xs={12}>
                <strong> {M.DOCUMENT_UPLOAD_FILE_TYPE}</strong>
              </Col>
              {selectedDocumentType.fileTypes.map(type =>
                M.DOCUMENT_UPLOAD_FILE_SIZE(
                  type,
                  selectedDocumentType.dims[type].maxSizeInKB,
                  selectedDocumentType.fileTypes.length === 1
                )
              )}
              <Col xs={12}>{M.DOCUMENT_UPLOAD_FILE_COUNT}</Col>
            </>
          )}
          <Col className="col-12 text-right mt-2">
            <Button
              className={styles.submitButton}
              onClick={handleSubmit(onSubmit)}
              disabled={disableSubmit}
            >
              {M.UPLOAD}
            </Button>
            <div className={classNames(styles.error, !error && "d-none")}>
              {error}
            </div>
          </Col>
        </Row>
      </CardWithTitle>
    </Col>
  );
};

UploadDocumentToVault.defaultProps = {
  onSubmit: () => null,
  documentTypes: [],
  selectedDocumentType: null,
};

UploadDocumentToVault.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disableSubmit: PropTypes.bool,
  documentTypes: PropTypes.array,
  selectedDocumentType: PropTypes.object,
  onChangeDocumentType: PropTypes.func,
};

export default compose(
  withPrompt,
  withNotifier,
  Banner.withBanner,
  withState("bannerId", "setBannerId", null),
  withAppLoader({
    loadFn: ({ dispatch, match }, fetchOptions) =>
      dispatch(
        fetchVaultDocumentTypesAndDims(
          { parcelCode: match.params.parcelCode },
          fetchOptions
        )
      ),
  }),
  connect(
    state => ({
      documentTypes: getVaultDocumentTypesKeyValue(state),
      selectedDocumentType: getSelectedVaultDocumentType(state),
    }),
    (dispatch, { parcel, banner, bannerId, setBannerId }) => ({
      onChangeDocumentType: (event, newValue) => {
        if (bannerId) {
          banner.hideById(bannerId);
        }

        if (
          newValue &&
          isDocumentTypeUploaded(parcel.vaultDocumentKey, newValue)
        ) {
          const bannerId = banner.showByType(BANNERS_TYPES.WARNING, {
            message: M.DOCUMENT_TYPE_ALREADY_UPLOADED,
          });
          setBannerId(bannerId);
        }
      },
    })
  ),
  withParcelEdit({
    onSubmit: (
      parcelCode,
      { documentType },
      { selectedDocumentType, documentBase64 }
    ) =>
      uploadDocumentToVault({
        parcelCode,
        documentType,
        format: selectedDocumentType.fileTypes[0],
        document: documentBase64,
        mps: selectedDocumentType.mps,
      }),
    validate: deliveryEditValidators.uploadDocumentToVault,
    asyncValidate: async values => {
      const document = values[Fields.DOCUMENT];

      if (document instanceof File && document.type === "application/pdf") {
        const isValidPdf = await isRealPdfFile(document);
        if (!isValidPdf) {
          throw { [Fields.DOCUMENT]: M.INVALID_PDF_FILE };
        }
      }
    },
    asyncBlurFields: [Fields.DOCUMENT],
    shouldError: () => true,
    shouldAsyncValidate: () => true,
  }),
  lifecycle({
    componentWillUnmount() {
      this.props.dispatch(clearVaultDocumentTypesAndDims());
      this.props.banner.hideById(this.props.bannerId);
      this.props.setBannerId(null);
    },
  })
)(UploadDocumentToVault);
